import { motion } from "framer-motion";
import { Check, Eye, FileCheck, Lock, ShieldCheck } from "lucide-react";
import { NOTE_LEGALI_URL, PRIVACY_URL, VARIANT } from "@/constants/links";

const isVip = VARIANT === "vip";

const PRINCIPLES = [
  {
    icon: Lock,
    iconBg: "bg-violet text-white shadow-lg shadow-violet/40",
    border: "border-violet/50",
    title: "Crittografia end-to-end",
    desc: "I dati della tua famiglia sono protetti e non vengono mai venduti a terzi.",
  },
  {
    icon: Eye,
    iconBg: "bg-sky text-white shadow-lg shadow-sky/40",
    border: "border-sky/50",
    title: "Nessun tracciamento clandestino",
    desc: "L'app opera con massima trasparenza, visibile anche sul dispositivo del ragazzo.",
  },
  {
    icon: FileCheck,
    iconBg: "bg-emerald-500 text-white shadow-lg shadow-emerald-500/40",
    border: "border-emerald-500/50",
    title: "Conforme GDPR UE",
    desc: "Sviluppata secondo le direttive europee più rigide per la tutela dei minori.",
  },
];

/**
 * FIX 2026-10-08 (beta report C3): per la variante VIP "nessun tracciamento
 * clandestino" era in contraddizione con la capacità reale `stealth` (cattura
 * camera in background senza interfaccia visibile). Il principio viene riscritto
 * in termini di responsabilità e controllo: la supervisione è un atto del
 * genitore sul dispositivo del figlio minore, mai sorveglianza occulta di terzi.
 */
const PRINCIPLES_VIP = [
  {
    icon: Lock,
    iconBg: "bg-violet text-white shadow-lg shadow-violet/40",
    border: "border-violet/50",
    title: "Crittografia end-to-end",
    desc: "I contenuti sensibili viaggiano cifrati: li decifra solo il tuo account genitore. Nessun dato viene venduto a terzi.",
  },
  {
    icon: Eye,
    iconBg: "bg-sky text-white shadow-lg shadow-sky/40",
    border: "border-sky/50",
    title: "Supervisione, non spionaggio",
    desc: "VIP è pensato per il genitore che supervisiona il dispositivo del figlio minore, sotto la propria responsabilità. Usarlo su adulti senza consenso è vietato e penalmente rilevante.",
  },
  {
    icon: FileCheck,
    iconBg: "bg-emerald-500 text-white shadow-lg shadow-emerald-500/40",
    border: "border-emerald-500/50",
    title: "Conforme GDPR UE",
    desc: "Titolarità in capo al genitore, consenso informato, funzioni sensibili in opt-in, diritto di esportazione e cancellazione dei dati.",
  },
];

const TRANSPARENCY = [
  "Non registra audio, schermo o fotocamera",
  "Il monitoraggio è sempre visibile sul dispositivo del figlio",
  "I messaggi social sul pannello arrivano dalle notifiche del telefono del figlio, non dalla chat completa",
];

/**
 * FIX 2026-10-08 (beta report C3, priorità CRITICA): la lista sopra era FALSA
 * per la variante VIP. Il registro feature reale (BACKEND app/core/features.py,
 * ANDROID core/common/FeatureFlags.kt) include ambient_audio, screenshots,
 * live_video, live_audio e stealth: il prodotto registra audio, schermo e
 * fotocamera, e GuardianChatCaptureService legge la chat WhatsApp completa
 * (anche i messaggi inviati e quelli eliminati), non solo le notifiche.
 * Pubblicare il testo vecchio esponeva a contestazioni GDPR e store.
 * La variante VIP usa quindi l'elenco accurato qui sotto.
 */
const TRANSPARENCY_VIP = [
  "VIP intercetta davvero ciò che elenca: chat complete, registro chiamate, posizione, siti visitati, utilizzo app e, su tua attivazione, audio, schermo e fotocamera",
  "Le funzioni più invasive (tastiera e cattura silenziosa) sono spente di default e richiedono un opt-in esplicito per singolo dispositivo",
  "Il genitore resta il titolare del trattamento: ogni capacità si disattiva dal pannello e i dati si esportano o si cancellano su richiesta",
];

const PRIVACY_IMG =
  "https://images.pexels.com/photos/4200824/pexels-photo-4200824.jpeg?auto=compress&cs=tinysrgb&w=1100&h=825&fit=crop";
const PRIVACY_SRC_SET = [
  "https://images.pexels.com/photos/4200824/pexels-photo-4200824.jpeg?auto=compress&cs=tinysrgb&w=480&h=360&fit=crop 480w",
  "https://images.pexels.com/photos/4200824/pexels-photo-4200824.jpeg?auto=compress&cs=tinysrgb&w=768&h=576&fit=crop 768w",
  `${PRIVACY_IMG} 1100w`,
].join(", ");

export const PrivacySection = () => (
  <section id="privacy" className="relative scroll-mt-24 overflow-hidden bg-white py-24 md:py-32">
    <div
      aria-hidden="true"
      className="absolute -right-40 top-10 h-[440px] w-[440px] rounded-full blur-3xl"
      style={{ background: "radial-gradient(closest-side, rgba(119,99,229,0.28), transparent)" }}
    />
    <div
      aria-hidden="true"
      className="absolute -left-24 bottom-24 h-40 w-40 bg-dots-pink rounded-3xl"
    />
    <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-5 sm:px-8 lg:grid-cols-2">
      <div>
        <span className="inline-flex items-center gap-2 rounded-full bg-violet px-4 py-2 text-xs font-extrabold uppercase tracking-widest text-white shadow-lg shadow-violet/30">
          Privacy
        </span>
        <h2 className="isolate mt-5 font-display text-3xl font-black tracking-tight text-ink sm:text-4xl lg:text-5xl">
          {isVip ? (
            <>
              Potere reale,{" "}
              <span className="text-highlight">responsabilità reale</span>.
            </>
          ) : (
            <>
              Tecnologia che <span className="text-highlight">protegge</span>, senza invadere.
            </>
          )}
        </h2>
        <p className="mt-5 max-w-lg text-base font-medium leading-relaxed text-ink/75 sm:text-lg">
          {isVip
            ? "VIP può leggere molto di ciò che passa sul dispositivo: per questo ti diciamo esattamente cosa raccoglie, senza ridimensionarlo. Le capacità più invasive restano spente finché non le attivi tu, e si spengono con un tocco dal pannello."
            : "Guardian Family raccoglie solo ciò che serve al parental control e spiega chiaramente quando una funzione è attiva. La fiducia è alla base della famiglia."}
        </p>

        <div className="mt-10 space-y-4">
          {(isVip ? PRINCIPLES_VIP : PRINCIPLES).map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              data-testid={`privacy-card-${i + 1}`}
              className={`flex items-start gap-4 rounded-3xl border-[3px] ${p.border} bg-white p-5 card-pop transition-all duration-300 hover:-translate-y-0.5`}
            >
              <span
                className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${p.iconBg}`}
              >
                <p.icon size={22} />
              </span>
              <div>
                <h3 className="font-display text-lg font-black tracking-tight text-ink">{p.title}</h3>
                <p className="mt-1 text-sm font-medium leading-relaxed text-ink/75">{p.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-6 text-sm font-bold">
          <a
            data-testid="privacy-informativa-link"
            href={PRIVACY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-violet underline decoration-violet/40 decoration-2 underline-offset-4 transition-colors hover:text-violet-dark"
          >
            Informativa privacy
          </a>
          <a
            data-testid="privacy-note-legali-link"
            href={NOTE_LEGALI_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-violet underline decoration-violet/40 decoration-2 underline-offset-4 transition-colors hover:text-violet-dark"
          >
            Note Legali
          </a>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.15 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="relative"
      >
        <div
          aria-hidden="true"
          className="absolute -left-10 -top-10 h-28 w-28 rounded-full bg-yellow"
        />
        <div
          aria-hidden="true"
          className="absolute -bottom-10 -right-10 h-32 w-32 rounded-full border-[14px] border-pinky-deep/70"
        />
        <div
          aria-hidden="true"
          className="absolute -left-8 bottom-24 h-20 w-20 bg-dots-violet rounded-2xl"
        />
        <div className="gloss-frame relative -rotate-1 overflow-hidden rounded-[3rem] border-8 border-white">
          <img
            src={PRIVACY_IMG}
            srcSet={PRIVACY_SRC_SET}
            sizes="(min-width: 1024px) 50vw, 100vw"
            alt="Bambini biondi sdraiati sul divano di casa mentre usano tablet e smartphone"
            width="1100"
            height="825"
            className="aspect-[4/3] h-full w-full object-cover"
            style={{ filter: "saturate(1.1) contrast(1.06)" }}
            loading="lazy"
            decoding="async"
          />
          <span className="absolute left-4 top-4 flex items-center gap-2 rounded-full bg-white/95 px-4 py-2 text-xs font-extrabold text-ink shadow-lg backdrop-blur">
            <ShieldCheck size={14} className="text-violet" />
            Privacy by design
          </span>
        </div>

        <div className="relative z-10 mx-5 -mt-12 rotate-1 rounded-3xl border-[3px] border-pinky-deep/50 bg-white p-6 shadow-[0_28px_56px_-20px_rgba(31,31,37,0.4)] sm:mx-8">
          <p className="font-display text-xs font-black uppercase tracking-[0.2em] text-pinky-deep">
            Per trasparenza
          </p>
          <ul className="mt-4 space-y-3">
            {(isVip ? TRANSPARENCY_VIP : TRANSPARENCY).map((t) => (
              <li key={t} className="flex items-start gap-3 text-sm font-medium leading-relaxed text-ink/85">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white shadow-md shadow-emerald-500/40">
                  <Check size={12} strokeWidth={3.5} />
                </span>
                {t}
              </li>
            ))}
          </ul>
        </div>
      </motion.div>
    </div>
  </section>
);
